import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { publishReplay, refCount } from 'rxjs/operators';
import { Observable } from 'rxjs/Rx';
import { ListMetadata } from '../../models/list-metadata.model';
import { Card, CardDomiciliationModel } from '../../models/loyalty-card.model';
import { HttpClientService } from '../httpClient/httpclient.service';
import { ResponseHandlerService } from './../httpClient/responseHandler.service';
import { AuthService } from './../user/auth.service';
import { MessageService } from 'primeng/primeng';


@Injectable()
export class LoyaltyCardService {

  public static readonly REASON_LIST_BLACKLIST_TYPE = 'BLACKLIST.TYPE';
  public static readonly REASON_LIST_SUBSTITUTION_TYPE = 'SUBSTITUTION.TYPE';
  public static readonly SELF_SCANNING_REASON_TYPE = 'SELFSCANNING.TYPE';

  private baseUrl = HttpClientService.BASE_MS_LOYALTY + '/cards';
  private baseUrlReason = HttpClientService.BASE_MS_LOYALTY + '/reason';
  private cachedFields: Observable<any>;
  private cardsLengthParamGroup = 'Card';

  constructor(
    private http: HttpClientService,
    private httpClient: HttpClient,
    private authService: AuthService,
    private srvService: ResponseHandlerService,
    private translate: TranslateService,
    private srvMessage: MessageService

  ) { }

  get(cardId): Observable<Card> {
    return this.http.get(this.baseUrl + '/' + cardId);
  }

  /**
    * Tramite metodo GET, restituisce la lista delle carte.
    * Prevede obbligatoriamente un offset ed un limite, permettendo la paginazione della lista.
  */
  list(
    offset = null,
    limit = null,
    search?: Array<{ key: string, value: string }>,
    globalSearch?: string,
    sort?: string,
    sort_dir?: number
  ): Observable<{ data: Array<Card>, metadata: ListMetadata }> {
    const params = HttpClientService.buildListParams(offset, limit, search, globalSearch, sort, sort_dir);
    const indexCd = search.findIndex(f => f.key === "customerDescription");
    const indexCn = search.findIndex(f => f.key === "card_no");
    //const indexSt = search.findIndex(f => f.key === "store");
    //const indexChD = search.findIndex(f => f.key === "channelDescription");
    const indexBt = search.findIndex(f => f.key === "birth_date");
    const indexStore = search.findIndex(f => f.key === "store");
    const indexCardId = search.findIndex(f => f.key === "cardId");
    const indexCardNo = search.findIndex(f => f.key === "cardNo");
    const indexCnId = search.findIndex(f => f.key === "card_id");


    //fix KS-6545 per lista carte
    if(indexCd == -1 &&
       indexCn == -1 &&
       indexStore == -1 &&
       //indexSt == -1 &&
       //indexChD == -1 &&
       indexBt == -1 && 
       indexCardId == -1 &&
       indexCardNo == -1 &&
       indexCnId == -1){
      this.srvMessage.add({severity: 'error', summary: this.translate.instant('common.warning'),
        detail: this.translate.instant('card.addFilterTable')});
      return;
    }
    return this.http.getPaged(this.baseUrl + '/getCardList' +
      (params.toString() ? '?' + params.toString().replace(/\+/gi, '%2B').replace(/'/gi, "''") : '' ));
  }

  /**
   * Aggiorna i dati della carta avente id = card.id
  */
  update(card: Card): Observable<Card> {
    return this.http.put(this.baseUrl + '/' + card.id, card);
  }

  /**
   * Crea una nuova carta
  */
  create(card: Card): Observable<Card> {
    return this.http.post(this.baseUrl + '/', card);
  }

  remove(cardId): Observable<Card> {
    return this.http.delete(this.baseUrl + '/' + cardId);
  }

  getReasonList(queryParams: object): Observable<any> {
    let params: HttpParams = new HttpParams();
    if (queryParams) {
      for (const key in queryParams) {
        params = params.set(key, queryParams[key]);
      }
    }
    return this.http.getPaged(this.baseUrlReason + '/getReasonList?' + params.toString());
  }


  domiciliate(domiciliation: CardDomiciliationModel) {
    return this.http.post((`${this.baseUrl}/${domiciliation.card_id}/domiciliate`), domiciliation)
  }

  massiveDomiciliate(domiciliations: CardDomiciliationModel[]) {
    return this.http.post((`${this.baseUrl}/massiveDomiciliation`), domiciliations)
  }

  blacklistCard(cardNo: string): Observable<Card> {
    return this.http.putAdv(this.baseUrl + '/setBlackList/' + cardNo, null, true);
  }

  getFields() {
    if (!this.cachedFields) {
      this.cachedFields = this.http.get('koncentro-loyalty/schema/CardDto').pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.cachedFields;
  }

  getExcelFilename(filters?: any[]) {
    let queryString = '';
    /* Se di tipo Array, filters corrisponde ai filtri della table e vengono inviati in query string,
       altrimenti ai filtri della ExtSearch, quindi vengono inviati come payload
    */
    let data = Array.isArray(filters) ? {} : filters;
    if (Array.isArray(filters) && filters.length > 0) {
      filters.forEach(filter => queryString += `${filter.key}=${filter.value}&`);
    }
    return this.http.post(queryString.length > 0 ? `/koncentro-loyalty/cards/exportCards?${queryString}` : '/koncentro-loyalty/cards/exportCards', data);
  }

  downloadExcel(filename: string): Observable<Blob> {
    const requestBody = {'filename': filename};
    const userCompany = this.authService.user.selectedCompany.toString();
    const token =  `Bearer ${this.authService.user.token}`;
    const language = this.authService.sessionHelper.selectedLanguage;
    const requestHeaders = {
      'Accept-Company': userCompany,
      'Content-Type': 'application/json',
      'Authorization': token,
      'Accept-Language': language
    };
    return this.httpClient.post(
      '/koncentro-loyalty/download',
      requestBody,
      {
        headers: requestHeaders,
        responseType: 'blob'
      }
    );
  }

  sendExtSearchQuery(data): Observable<{ data: any, metadata: ListMetadata }> {
    if (data.sortField && data.sortDir) {
      return this.http.postPaged(`${this.baseUrl}/getCardList?offset=${data.offset}&limit=${data.limit}&sort=${data.sortField}&sort_dir=${data.sortDir}`, data.query);
    } else {
      return this.http.postPaged(`${this.baseUrl}/getCardList?offset=${data.offset}&limit=${data.limit}`, data.query);
    }
  }

  /**
   * Metodo per ottenere tramite la Param la lunghezza per il filtri delle carte
   * @returns
   */
  getCardsLength() {
    return this.http.get(`${HttpClientService.BASE_MS_LOYALTY}/params/?param_group=${this.cardsLengthParamGroup}`);
  }

  getReasonListWithParams(search?: Array<{ key: string, value: string }>): Observable<any> {
    const params: URLSearchParams = new URLSearchParams();
    if ( search ) {
      for (const filter of search) {
        params.set(filter.key, filter.value);
      }
    }
    return this.http.getPaged(this.baseUrlReason + '/getReasonList?' + params.toString());
  }
}



// WEBPACK FOOTER //
// ./src/app/services/loyalty/loyalty-card.service.ts